import { useEffect } from "react";

import type {
  ConditionalConfig,
  FormFields,
  FormPageType,
  FormState,
  FormStep,
  OptionObject,
  PageVisibilityContext,
} from "~/types";
import { toIntegerCurrency, toWholeCurrency } from "~/util/currency";
import {
  fieldHasConditional,
  getPagesFromFields,
  getVisiblePages,
  isPageVisible,
  standardiseValue,
} from "~/util/formHelpers";
import {
  ensureMinimumItems,
  joinRepeaterArrayToString,
  parseStringToRepeaterArray,
} from "~/util/stringRepeaterUtils";

export const createEmptyFormState = (
  fields: FormFields
): Record<string, unknown> => {
  const state: Record<string, unknown> = {};

  // First pass: initialize regular fields including repeaters
  fields?.forEach((field) => {
    // Only access field.name if blockType is one that supports 'name'
    if ("name" in field && typeof field.name === "string" && field.name) {
      switch (field.blockType) {
        case "checkbox": {
          // Convert standardiseValue result to string for FormState
          const checkboxValue = standardiseValue(field.defaultValue);
          state[field.name] = String(checkboxValue);
          break;
        }
        case "checkboxGroup": {
          const options = (field.checkboxOptions || []).map((option) => {
            if (
              option.defaultValue === true ||
              (typeof option.defaultValue === "string" &&
                option.defaultValue === "true")
            ) {
              return option.value;
            } else {
              return false;
            }
          }) as string[];

          state[field.name] = options;
          break;
        }
        case "select":
        case "radioGroup": {
          // Get the options
          // If select then options are defined in the field
          let options: OptionObject[] = [];

          if (field.blockType === "select") {
            options = field.selectOptions || [];
          }

          if (field.blockType === "radioGroup") {
            options = field.radioOptions || [];
          }

          const selectedItem = field.defaultValue
            ? options.find((option) => option.id === field.defaultValue)
            : undefined;

          // If the selected item is found, use its value; otherwise, use the default value
          state[field.name] = selectedItem?.value || "";
          break;
        }
        case "currency":
          state[field.name] =
            field.defaultValue !== undefined
              ? toWholeCurrency(Number(field.defaultValue))
              : "";
          break;
        case "number":
          state[field.name] =
            field.defaultValue !== undefined ? Number(field.defaultValue) : "";
          break;
        case "repeater": {
          // For repeaters, check if minBlocks is set and create default entries
          const minBlocks = field.minBlocks || 0;
          const defaultBlocks: Record<string, unknown>[] = [];

          // Create at least minBlocks entries
          for (let i = 0; i < minBlocks; i++) {
            if (field.blocks && field.blocks.length > 0) {
              // Create a default entry for each block in the repeater
              const blockDefaults: Record<string, unknown> = {};
              field.blocks.forEach((block) => {
                const blockState = createEmptyFormState([block]);
                Object.assign(blockDefaults, blockState);
              });
              defaultBlocks.push(blockDefaults);
            }
          }

          state[field.name] = defaultBlocks;
          break;
        }
        default:
          state[field.name] =
            "defaultValue" in field ? String(field.defaultValue ?? "") : "";
      }
    }
  });

  // Second pass: handle dynamic page sections
  fields?.forEach((field) => {
    if (field.blockType === "dynamicPageSection") {
      // Type guard to ensure we have the correct field type
      if (!("repeaterSource" in field) || !("template" in field)) {
        return;
      }

      // Find the repeater field
      const repeaterField = fields.find(
        (f) => f.name === field.repeaterSource.nameField
      );

      if (
        repeaterField &&
        repeaterField.blockType === "repeater" &&
        repeaterField.name
      ) {
        // Check if repeater data exists
        const repeaterData = state[repeaterField.name] as unknown[];

        if (Array.isArray(repeaterData)) {
          // Initialize arrays for each field in the template
          field.template.fields.forEach((templateField) => {
            if ("name" in templateField && templateField.name) {
              // Initialize an array with empty objects for each repeater item
              state[templateField.name] = repeaterData.map(() => {
                return "";
              });
            }
          });
        }
      }
    }
  });

  return state;
};

export const mergeFormState = (
  currentState: FormState,
  newData: FormState
): FormState => {
  return { ...currentState, ...newData };
};

export const findFirstIncompleteRequiredPageIndex = ({
  pages,
  formState,
}: {
  pages: FormPageType[];
  formState: FormState;
}): number => {
  for (let i = 0; i < pages.length; i++) {
    const formPage = pages[i];

    for (const field of formPage.fields ?? []) {
      if (
        "required" in field &&
        field.required &&
        "name" in field &&
        field.name &&
        (!formState[field.name] || formState[field.name] === "")
      ) {
        return i;
      }

      // Check fields with minBlocks (repeaters, signersSelector)
      if (
        "minBlocks" in field &&
        typeof field.minBlocks === "number" &&
        field.minBlocks >= 1 &&
        "name" in field &&
        field.name
      ) {
        const value = formState[field.name];
        if (!Array.isArray(value) || value.length < field.minBlocks) {
          return i;
        }
      }
    }
  }

  return -1; // All visible steps are complete
};

/**
 * Check if a page has any required fields
 */
const pageHasRequiredFields = (page: FormPageType): boolean => {
  if (!page.fields || page.fields.length === 0) {
    return false;
  }

  return page.fields.some(
    (field) =>
      ("required" in field && field.required === true) ||
      ("minBlocks" in field &&
        typeof field.minBlocks === "number" &&
        field.minBlocks >= 1)
  );
};

/**
 * Check if all required fields in a page are complete
 */
const pageRequiredFieldsComplete = (
  page: FormPageType,
  formState: FormState
): boolean => {
  if (!page.fields) return true;

  for (const field of page.fields) {
    if (
      "required" in field &&
      field.required &&
      "name" in field &&
      field.name &&
      (!formState[field.name] || formState[field.name] === "")
    ) {
      return false;
    }

    // Check fields with minBlocks (repeaters, signersSelector)
    if (
      "minBlocks" in field &&
      typeof field.minBlocks === "number" &&
      field.minBlocks >= 1 &&
      "name" in field &&
      field.name
    ) {
      const value = formState[field.name];
      if (!Array.isArray(value) || value.length < field.minBlocks) {
        return false;
      }
    }
  }

  return true;
};

export const pageStatus = ({
  currentSlug,
  formState,
  name,
  pages,
  visitedPages,
}: {
  currentSlug: string;
  formState: FormState; // CHANGED: From array to typed object
  name: string;
  pages: FormPageType[];
  visitedPages?: Set<string>;
}): FormStep["status"] => {
  if (name === currentSlug) {
    return "current";
  }

  // If page hasn't been visited, it's always incomplete
  if (!visitedPages || !visitedPages.has(name)) {
    return "incomplete";
  }

  // Page has been visited - check completion based on requirements
  const targetPage = pages.find((page) => page.name === name);
  if (!targetPage) {
    return "incomplete";
  }

  // Check if page has required fields
  if (!pageHasRequiredFields(targetPage)) {
    // No required fields and visited = complete
    return "complete";
  }

  // Has required fields - check if they're complete
  if (pageRequiredFieldsComplete(targetPage, formState)) {
    return "complete";
  }

  return "incomplete";
};

export const pageSteps = ({
  pages,
  currentSlug,
  formState,
  visitedPages,
}: {
  pages: FormPageType[];
  currentSlug: string;
  formState: FormState; // CHANGED: From array to typed object
  visitedPages?: Set<string>;
}): FormStep[] =>
  pages.map((page) => ({
    label: page.label,
    name: page.name,
    status: pageStatus({
      currentSlug,
      formState,
      name: page.name,
      pages,
      visitedPages,
    }),
    isSubPage: page.subPage ?? false,
  }));

export const getStepsFromFields = ({
  fields,
  currentSlug,
  formState,
  visitedPages,
}: {
  fields: FormFields;
  currentSlug: string;
  formState: FormState;
  visitedPages?: Set<string>;
}): FormStep[] => {
  const visiblePages = getVisiblePages(fields, formState);
  return pageSteps({
    pages: visiblePages,
    currentSlug,
    formState,
    visitedPages,
  });
};

export const canUserNavigateToStep = ({
  step,
  pages,
  formState,
}: {
  step: string;
  pages: FormPageType[];
  formState: FormState; // CHANGED: From array to typed object
}) => {
  const incompletePageIndex = findFirstIncompleteRequiredPageIndex({
    pages,
    formState,
  });

  // If user has completed the form, they can access any step.
  if (incompletePageIndex === -1) {
    return { incompletePageIndex, canAccess: true };
  }

  // If a step "preview" is requested, the user cannot access at this point.
  // They should have been directed in the above check.
  if (step === "preview") {
    return { incompletePageIndex, canAccess: false };
  }

  // Check if the user is already trying to navigate to a step that is before
  // or at the incomplete step (they need to access it to fill it out).
  const stepIndex = pages.findIndex((page: FormPageType) => page.name === step);
  const canAccessStep = stepIndex <= incompletePageIndex;

  return { incompletePageIndex, canAccess: canAccessStep };
};

const cleanUpAndToInt = (value: unknown) => {
  if (!value) return "";

  const number = Number(value);

  return toIntegerCurrency(number);
};

export const formObjectFormattingToBackend = (
  formObject: FormState,
  fields: FormFields
) => {
  // Turn formObject to array to loop over.
  const formObjectAsArray = Object.entries(formObject);

  // Create result object to store converted values
  const convertedState: Record<string, unknown> = {};

  // Loop through the formObject and find the associated field in the fields array.
  formObjectAsArray.forEach((state: [string, unknown]) => {
    // state is [key, value] pair from Object.entries()
    const stateKey = state[0];
    const stateValue = state[1]; // This is the actual value

    // Preserve metadata keys that start with double underscore
    if (stateKey.startsWith("__")) {
      convertedState[stateKey] = stateValue;
      return;
    }

    if (!fields) {
      return;
    }

    const field = fields.find((field) => {
      // Remove non usable fields from this process. Fix TypeScript error.
      if (
        field.blockType === "pageSection" ||
        field.blockType === "dateRange" ||
        field.blockType === "message" ||
        field.blockType === "subHeading" ||
        field.blockType === "dynamicPageSection" ||
        field.blockType === "reviewPropertyDetails" ||
        field.blockType === "reviewLeaseDetails" ||
        field.blockType === "reviewCollectRentDetails" ||
        field.blockType === "reviewPaymentSchedule" ||
        field.blockType === "reviewUnitDetails"
      ) {
        return false;
      }

      if (field.blockType === "documentName") {
        if (stateKey === "userDefinedDocumentName") {
          return true;
        }
        return false;
      }

      if (stateKey === field.name) {
        return true;
      }

      return false;
    });

    // If no field is found, skip this state
    if (!field) {
      return;
    }

    if (field?.blockType === "currency") {
      convertedState[stateKey] = cleanUpAndToInt(stateValue);
    } else if (field?.blockType === "prefillLease") {
      convertedState[stateKey] = {
        ...(typeof stateValue === "object" && stateValue !== null
          ? stateValue
          : {}),
        rent: cleanUpAndToInt((stateValue as Record<string, unknown>)?.rent),
        securityDeposit: cleanUpAndToInt(
          (stateValue as Record<string, unknown>)?.securityDeposit
        ),
      };
    } else if (field?.blockType === "stringRepeater") {
      // Convert array of {value: string} objects back to delimited string
      const delimiter = field.delimiter || ", ";
      convertedState[stateKey] = joinRepeaterArrayToString(
        stateValue as Array<{ value: string }>,
        delimiter
      );
    } else {
      convertedState[stateKey] = stateValue as string;
    }
  });

  return convertedState;
};

export const formObjectFormattingToFrontend = (
  formObject: FormState,
  fields: FormFields
): FormState => {
  const formObjectAsArray = Object.entries(formObject);

  const convertedState: Record<string, unknown> = {};

  formObjectAsArray.forEach((state: [string, unknown]) => {
    // state is [key, value] pair from Object.entries()
    const stateKey = state[0];
    const stateValue = state[1]; // This is the actual value

    // Preserve metadata keys that start with double underscore
    if (stateKey.startsWith("__")) {
      convertedState[stateKey] = stateValue;
      return;
    }

    if (!fields) {
      return;
    }

    const field = fields.find((field) => {
      // Remove non usable fields from this process. Fix TypeScript error.
      if (
        field.blockType === "pageSection" ||
        field.blockType === "dateRange" ||
        field.blockType === "message" ||
        field.blockType === "subHeading" ||
        field.blockType === "dynamicPageSection" ||
        field.blockType === "reviewPropertyDetails" ||
        field.blockType === "reviewUnitDetails" ||
        field.blockType === "reviewLeaseDetails" ||
        field.blockType === "reviewCollectRentDetails" ||
        field.blockType === "reviewPaymentSchedule"
      ) {
        return false;
      }

      if (field.blockType === "documentName") {
        if (stateKey === "userDefinedDocumentName") {
          return true;
        }
        return false;
      }

      if (stateKey === field.name) {
        return true;
      }

      return false;
    });

    // If no field is found, skip this state
    if (!field) {
      return;
    }

    if (field?.blockType === "currency") {
      convertedState[stateKey] = toWholeCurrency(Number(stateValue));
    } else if (field?.blockType === "prefillLease") {
      convertedState[stateKey] = {
        ...(typeof stateValue === "object" && stateValue !== null
          ? stateValue
          : {}),
        rent: toWholeCurrency(
          (stateValue as Record<string, unknown>)?.rent as number
        ),
        securityDeposit: toWholeCurrency(
          (stateValue as Record<string, unknown>)?.securityDeposit as number
        ),
      };
    } else if (field?.blockType === "stringRepeater") {
      // Convert delimited string to array of {value: string} objects
      const delimiter = field.delimiter || ", ";
      const minItems = field.minBlocks || 0;
      const items = parseStringToRepeaterArray(String(stateValue), delimiter);
      convertedState[stateKey] = ensureMinimumItems(
        items,
        Math.max(minItems, 1)
      );
    } else if (
      field?.blockType === "address" &&
      typeof stateValue === "object" &&
      stateValue !== null
    ) {
      // Preserve address objects as-is
      convertedState[stateKey] = stateValue;
    } else {
      convertedState[stateKey] = String(stateValue);
    }
  });

  return convertedState;
};

export const prefillFormattingToFrontend = (
  prefillData: Record<string, unknown>,
  fields: FormFields
): FormState => {
  const convertedPrefillData: Record<string, unknown> = {};

  // Return empty object if no data to process
  if (!prefillData || !fields || Object.keys(prefillData).length === 0) {
    return convertedPrefillData;
  }

  // Process each prefill value
  Object.entries(prefillData).forEach(([key, value]) => {
    // Find the field that matches this prefill key
    const field = fields.find((field) => {
      // Skip non-input fields
      if (
        field.blockType === "pageSection" ||
        field.blockType === "dateRange" ||
        field.blockType === "message" ||
        field.blockType === "subHeading" ||
        field.blockType === "dynamicPageSection" ||
        field.blockType === "reviewPropertyDetails" ||
        field.blockType === "reviewUnitDetails" ||
        field.blockType === "reviewLeaseDetails" ||
        field.blockType === "reviewCollectRentDetails" ||
        field.blockType === "reviewPaymentSchedule"
      ) {
        return false;
      }

      if (
        field.blockType === "documentName" &&
        key === "userDefinedDocumentName"
      ) {
        return true;
      }

      return "name" in field && field.name === key;
    });

    // If no matching field found, skip this prefill value
    if (!field) return;

    // Convert the value based on field type
    if (field.blockType === "currency") {
      // Convert to whole currency format (e.g., "2000" -> "2,000")
      convertedPrefillData[key] = toWholeCurrency(Number(value));
    } else if (field.blockType === "number") {
      convertedPrefillData[key] = Number(value);
    } else if (field.blockType === "checkbox") {
      // Convert to boolean string
      convertedPrefillData[key] = String(value === "true" || value === true);
    } else if (
      field.blockType === "select" ||
      field.blockType === "radioGroup"
    ) {
      // For select/radio, the value should match one of the option values
      convertedPrefillData[key] = String(value);
    } else if (field.blockType === "checkboxGroup") {
      // Handle checkbox groups - expect comma-separated values or array
      if (Array.isArray(value)) {
        convertedPrefillData[key] = value;
      } else if (typeof value === "string") {
        convertedPrefillData[key] = value.split(",").map((v) => v.trim());
      }
    } else if (field.blockType === "repeater") {
      // Handle repeater fields if needed
      if (Array.isArray(value)) {
        convertedPrefillData[key] = value;
      }
    } else if (field.blockType === "stringRepeater") {
      // Convert string to repeater array format
      const delimiter = field.delimiter || ", ";
      const minItems = field.minBlocks || 0;
      const items = parseStringToRepeaterArray(String(value), delimiter);
      convertedPrefillData[key] = ensureMinimumItems(
        items,
        Math.max(minItems, 1)
      );
    } else {
      // Default to string for text, textarea, date, etc.
      convertedPrefillData[key] = String(value);
    }
  });

  return convertedPrefillData;
};

/**
 * Helper to extract field names from a ConditionalConfig
 */
const extractFieldNamesFromConditional = (
  cond: ConditionalConfig | undefined,
  targetSet: Set<string>
): void => {
  if (!cond?.enableConditional) return;

  // Handle base conditional
  if ("fieldSelector" in cond && cond.fieldSelector) {
    const { fieldName } = parseFieldSelector(cond.fieldSelector);
    targetSet.add(fieldName);
  }

  // Handle combined conditional
  if ("conditions" in cond && Array.isArray(cond.conditions)) {
    cond.conditions.forEach((condition) => {
      if (condition.fieldSelector) {
        const { fieldName } = parseFieldSelector(condition.fieldSelector);
        targetSet.add(fieldName);
      }
    });
  }

  // Handle grouped conditional
  if ("grp" in cond && Array.isArray(cond.grp)) {
    cond.grp.forEach((group) => {
      group.cond.forEach((condition) => {
        if (condition.fieldSelector) {
          const { fieldName } = parseFieldSelector(condition.fieldSelector);
          targetSet.add(fieldName);
        }
      });
    });
  }
};

/**
 * Get the names of fields that control conditionals for other fields or pages
 * These are the only fields that need real-time syncing to parent state
 */
export const getConditionalControllerFieldNames = (
  allFields: FormFields
): Set<string> => {
  const controllerFieldNames = new Set<string>();

  if (!allFields) return controllerFieldNames;

  // Find all fields referenced in conditionals (visibility and conditionalRequired)
  allFields?.forEach((field) => {
    // Check visibility conditional (cond)
    if (fieldHasConditional(field)) {
      extractFieldNamesFromConditional(field.cond, controllerFieldNames);
    }

    // Check conditionalRequired
    if ("conditionalRequired" in field && field.conditionalRequired) {
      extractFieldNamesFromConditional(
        field.conditionalRequired as ConditionalConfig,
        controllerFieldNames
      );
    }
  });

  return controllerFieldNames;
};
// Helper to get pages that affect visibility
export const getConditionalControllers = (
  allPages: FormPageType[]
): string[] => {
  const controllers = new Set<string>();

  allPages.forEach((page) => {
    if (page.cond?.enableConditional) {
      // Handle base conditional
      if ("fieldSelector" in page.cond && page.cond.fieldSelector) {
        // Parse the field selector to get the base field name/slug
        const { fieldName } = parseFieldSelector(page.cond.fieldSelector);
        controllers.add(fieldName);
      }

      // Handle combined conditional
      if ("conditions" in page.cond && Array.isArray(page.cond.conditions)) {
        page.cond.conditions.forEach((condition) => {
          if (condition.fieldSelector) {
            // Parse the field selector to get the base field name/slug
            const { fieldName } = parseFieldSelector(condition.fieldSelector);
            controllers.add(fieldName);
          }
        });
      }

      // Handle grouped conditional
      if ("grp" in page.cond && Array.isArray(page.cond.grp)) {
        page.cond.grp.forEach((group) => {
          group.cond.forEach((condition) => {
            if (condition.fieldSelector) {
              // Parse the field selector to get the base field name/slug
              const { fieldName } = parseFieldSelector(condition.fieldSelector);
              controllers.add(fieldName);
            }
          });
        });
      }
    }
  });

  return Array.from(controllers);
};

// Optimized visibility check that only recalculates when needed
export const getVisiblePagesOptimized = (
  allFields: FormFields,
  formState: FormState,
  prevVisiblePages?: FormPageType[],
  changedFields?: string[]
): FormPageType[] => {
  if (!allFields) return [];

  const allPages = getPagesFromFields(allFields);

  // If we have previous results and know what changed, optimize
  if (prevVisiblePages && changedFields) {
    const controllers = getConditionalControllers(allPages);
    const hasRelevantChanges = changedFields.some((field) =>
      controllers.includes(field)
    );

    // No relevant changes = return cached result
    if (!hasRelevantChanges) {
      return prevVisiblePages;
    }
  }

  // Calculate fresh visibility
  const context: PageVisibilityContext = { formState, allFields };
  return allPages.filter((page) => isPageVisible(page, context));
};

// Helper to filter form state to visible fields only
export const getVisibleFormState = (
  flatState: FormState,
  visiblePages: FormPageType[]
): FormState => {
  const visibleState: FormState = {};
  const visibleFieldNames = new Set(
    visiblePages.flatMap(
      (page) =>
        page.fields
          ?.map((field) =>
            "name" in field && typeof field.name === "string"
              ? field.name
              : undefined
          )
          .filter(Boolean) || []
    )
  );

  Object.keys(flatState).forEach((fieldName) => {
    if (visibleFieldNames.has(fieldName)) {
      visibleState[fieldName] = flatState[fieldName];
    }
  });

  return visibleState;
};

// Helper to parse field selectors with dot notation (e.g., "property-selector.name")
const parseFieldSelector = (
  selector: string
): { fieldName: string; propertyPath?: string } => {
  const parts = selector.split(".");
  if (parts.length === 1) {
    return { fieldName: selector };
  }
  return {
    fieldName: parts[0],
    propertyPath: parts.slice(1).join("."),
  };
};

// Validate conditional configuration at runtime
export const validateConditionalConfiguration = (
  fields: FormFields
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  fields?.forEach((field) => {
    if (field.blockType === "pageSection" && field.cond?.enableConditional) {
      const cond = field.cond;

      // Only check for fieldSelector if it exists on the conditional object
      if ("fieldSelector" in cond) {
        if (!cond.fieldSelector) {
          errors.push(
            `Page "${field.label}" has conditional enabled but no field selector`
          );
        } else {
          // Parse the field selector to handle dot notation
          const { fieldName } = parseFieldSelector(cond.fieldSelector);
          // Verify the referenced field exists using the base field name/slug
          const referencedField = fields.find((f) => f.name === fieldName);
          if (!referencedField) {
            errors.push(
              `Page "${field.label}" references non-existent field: ${cond.fieldSelector}`
            );
          }
        }
      }

      // Only check for 'value' if not a CombinedConditional (i.e., if 'conditions' is not present)
      // and if the conditionalIs requires a value (equal/not-equal)
      if (
        !("conditions" in cond) &&
        "conditionalIs" in cond &&
        (cond.conditionalIs === "equal" ||
          cond.conditionalIs === "not-equal") &&
        (cond.value === undefined || cond.value === null)
      ) {
        errors.push(
          `Page "${field.label}" has conditional enabled but no value specified`
        );
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Get all pages from fields
export const getAllPages = (fields: FormFields) => {
  return fields?.filter((field) => field.blockType === "pageSection") || [];
};

// Type guard for conditional pages
export const isConditionalPage = (page: FormPageType): boolean => {
  return !!page.cond?.enableConditional;
};

// Type guard for valid conditional configuration
export const hasValidConditional = (page: FormPageType): boolean => {
  if (!page.cond?.enableConditional) return true;

  // For exists/not-exists, value is not required
  const requiresValue =
    "conditionalIs" in page.cond &&
    (page.cond.conditionalIs === "equal" ||
      page.cond.conditionalIs === "not-equal");

  return !!(
    "fieldSelector" in page.cond &&
    page.cond.fieldSelector &&
    (!requiresValue || page.cond.value !== undefined) &&
    page.cond.conditionalIs
  );
};

// Development-only validation hook
export const useFormValidation = (fields: FormFields) => {
  useEffect(() => {
    if (import.meta.env.VITE_APP_ENVIRONMENT === "development") {
      const validation = validateConditionalConfiguration(fields);
      if (!validation.isValid) {
        console.warn("Form configuration issues found:", validation.errors);
      }
    }
  }, [fields]);
};
